import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert } from '@mui/material';
import { getLiveTransactions, deleteTransaction, deleteAllOldTransactions } from 'apis/menuAPIs';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[50],
  },
  headerCell: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  totalPrice: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const LiveTransactions = () => {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  
  const queryClient = useQueryClient();

  const {
    data: transactions = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['liveTransactions'],
    queryFn: getLiveTransactions,
    refetchInterval: 30000, // Refetch every 30 seconds for live updates
    staleTime: 10000, // Data considered stale after 10 seconds
    retry: 2,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTransaction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liveTransactions'] });
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    },
    onError: (error) => {
      console.error('Error deleting transaction:', error);
      // You could add a toast notification here for error feedback
    }
  });

  const deleteAllMutation = useMutation({
    mutationFn: deleteAllOldTransactions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liveTransactions'] });
      setDeleteAllDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error deleting all old transactions:', error);
      // You could add a toast notification here for error feedback
    }
  });

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (transactionToDelete) {
      deleteMutation.mutate(transactionToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTransactionToDelete(null);
  };

  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    deleteAllMutation.mutate();
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    
    let time;
    if (timestamp._seconds) {
      // Firebase timestamp format
      time = new Date(timestamp._seconds * 1000);
    } else {
      // Standard timestamp
      time = new Date(timestamp);
    }
    
    const dateStr = time.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
    });
    
    const timeStr = time.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    
    return `${dateStr} ${timeStr}`;
  };

  const formatPrice = (price, currencySymbol = '$') => {
    if (typeof price !== 'number') return '-';
    return `${currencySymbol}${price.toFixed(2)}`;
  };

  const filterTransactionsByType = (transactions, tabIndex) => {
    if (!Array.isArray(transactions)) return [];

    switch (tabIndex) {
      case 0: // Dine In
        return transactions.filter(tx => tx.isDinein === true);
      case 1: // Pickups
        return transactions.filter(tx => 
          tx.isOnlineTakeawayOrPickup === true && !tx.isTakeawayDelivery
        );
      case 2: // Takeaway
        return transactions.filter(tx => 
          tx.table?.isTakeaway === true || (tx.isOnlineTakeawayOrPickup === true && !tx.isTakeawayDelivery)
        );
      case 3: // Deliveries
        return transactions.filter(tx => 
          tx.isTakeawayDelivery === true || tx.table?.isDeliveryOrder === true
        );
      default:
        return transactions;
    }
  };

  const getCurrentTransactions = () => {
    return filterTransactionsByType(transactions, selectedTab);
  };

  const renderTransactionTable = (filteredTransactions) => {
    if (filteredTransactions.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="h6" gutterBottom>
            No transactions found
          </Typography>
          <Typography variant="body2">
            There are currently no active transactions for this category.
          </Typography>
        </div>
      );
    }

    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell className={classes.headerCell}>Table/Order</TableCell>
              <TableCell className={classes.headerCell}>Total Price</TableCell>
              <TableCell className={classes.headerCell}>Start Time</TableCell>
              <TableCell className={classes.headerCell}>Status</TableCell>
              <TableCell className={classes.headerCell}>Items</TableCell>
              <TableCell className={classes.headerCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((transaction) => (
              <TableRow 
                key={transaction.transactionId || transaction.id} 
                className={classes.tableRow}
              >
                <TableCell>
                  <Typography variant="body1" component="div">
                    {transaction.tableNumber || transaction.id || 'N/A'}
                  </Typography>
                  {transaction.table?.takeawayPerson && (
                    <Typography variant="body2" color="textSecondary">
                      {transaction.table.takeawayPerson}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography className={classes.totalPrice}>
                    {formatPrice(
                      transaction.table?.totalSpending || transaction.table?.balance,
                      transaction.currencySymbolString
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  {formatTime(transaction.timestamp || transaction.table?.timeStamp)}
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color={transaction.table?.billPaid ? "primary" : "textSecondary"}
                  >
                    {transaction.table?.billPaid ? 'Paid' : 'Active'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {transaction.table?.orders?.reduce((total, order) => 
                      total + (order.dishes?.length || 0), 0
                    ) || 0} items
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="secondary"
                    onClick={() => handleDeleteClick(transaction)}
                    disabled={deleteMutation.isLoading}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const getTabCounts = () => {
    return [
      filterTransactionsByType(transactions, 0).length, // Dine In
      filterTransactionsByType(transactions, 1).length, // Pickups
      filterTransactionsByType(transactions, 2).length, // Takeaway
      filterTransactionsByType(transactions, 3).length, // Deliveries
    ];
  };

  if (isLoading) {
    return (
      <Box className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={50} />
        </div>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box className={classes.root}>
        <Alert 
          severity="error" 
          action={
            <button onClick={() => refetch()}>
              Retry
            </button>
          }
        >
          Error loading live transactions: {error?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  const tabCounts = getTabCounts();
  const currentTransactions = getCurrentTransactions();

  return (
    <Box className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Live Transactions
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDeleteAllClick}
          disabled={deleteAllMutation.isLoading || transactions.length === 0}
          startIcon={<DeleteIcon />}
        >
          {deleteAllMutation.isLoading ? 'Deleting...' : 'Delete All Old'}
        </Button>
      </Box>
      
      <Paper className={classes.paper}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label={`Dine In (${tabCounts[0]})`} />
          <Tab label={`Pickups (${tabCounts[1]})`} />
          <Tab label={`Takeaway (${tabCounts[2]})`} />
          <Tab label={`Deliveries (${tabCounts[3]})`} />
        </Tabs>
      </Paper>

      <Paper>
        {renderTransactionTable(currentTransactions)}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete Transaction
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this transaction? This action cannot be undone.
            {transactionToDelete && (
              <Box mt={1}>
                <strong>Transaction:</strong> {transactionToDelete.tableNumber || transactionToDelete.id}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCancel} 
            color="primary"
            disabled={deleteMutation.isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="secondary" 
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog
        open={deleteAllDialogOpen}
        onClose={handleDeleteAllCancel}
        aria-labelledby="delete-all-dialog-title"
        aria-describedby="delete-all-dialog-description"
      >
        <DialogTitle id="delete-all-dialog-title">
          Confirm Delete All Old Transactions
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-all-dialog-description">
            Are you sure you want to delete all old transactions? This action cannot be undone.
            <Box mt={1}>
              <strong>Warning:</strong> This will permanently remove all old transaction records from the system.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteAllCancel} 
            color="primary"
            disabled={deleteAllMutation.isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAllConfirm} 
            color="secondary" 
            variant="contained"
            disabled={deleteAllMutation.isLoading}
          >
            {deleteAllMutation.isLoading ? 'Deleting...' : 'Delete All Old'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveTransactions;